import React, { useState } from 'react';
import { useAccessibility } from '../../contexts/AccessibilityContext';
import { AccessibleModal } from './AccessibleModal';
import { useUniqueId } from '../../utils/accessibility';

interface AccessibilitySettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Accessibility settings modal that allows users to configure
 * accessibility preferences like high contrast mode, reduced motion, etc.
 */
export const AccessibilitySettings: React.FC<AccessibilitySettingsProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    isHighContrast,
    toggleHighContrast,
    prefersReducedMotion,
    toggleReducedMotion,
    colorScheme,
    setColorScheme,
    announce,
  } = useAccessibility();

  // Generate unique IDs for form elements
  const highContrastId = useUniqueId('high-contrast');
  const reducedMotionId = useUniqueId('reduced-motion');
  const colorSchemeId = useUniqueId('color-scheme');

  const handleHighContrastToggle = () => {
    toggleHighContrast();
    announce(
      `High contrast mode ${!isHighContrast ? 'enabled' : 'disabled'}`,
      true
    );
  };

  const handleReducedMotionToggle = () => {
    toggleReducedMotion();
    announce(
      `Reduced motion ${!prefersReducedMotion ? 'enabled' : 'disabled'}`,
      true
    );
  };

  const handleColorSchemeChange = (scheme: 'light' | 'dark' | 'system') => {
    setColorScheme(scheme);
    announce(`Color scheme changed to ${scheme}`, true);
  };

  return (
    <AccessibleModal
      isOpen={isOpen}
      onClose={onClose}
      title="Accessibility Settings"
      description="Configure accessibility preferences to improve your experience"
      size="md"
    >
      <div className="space-y-6">
        {/* High Contrast Mode */}
        <div className="accessible-form-field">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <label
                htmlFor={highContrastId}
                className="text-sm font-medium text-gray-900"
              >
                High Contrast Mode
              </label>
              <p className="text-sm text-gray-600 mt-1">
                Increases contrast between text and background colors for better readability
              </p>
            </div>
            <button
              id={highContrastId}
              type="button"
              onClick={handleHighContrastToggle}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible-enhanced ${
                isHighContrast ? 'bg-blue-600' : 'bg-gray-200'
              }`}
              role="switch"
              aria-checked={isHighContrast}
              aria-describedby={`${highContrastId}-description`}
            >
              <span className="sr-only">Toggle high contrast mode</span>
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  isHighContrast ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <div id={`${highContrastId}-description`} className="sr-only">
            High contrast mode is currently {isHighContrast ? 'enabled' : 'disabled'}
          </div>
        </div>

        {/* Reduced Motion */}
        <div className="accessible-form-field">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <label
                htmlFor={reducedMotionId}
                className="text-sm font-medium text-gray-900"
              >
                Reduced Motion
              </label>
              <p className="text-sm text-gray-600 mt-1">
                Reduces animations and transitions that may cause motion sensitivity
              </p>
            </div>
            <button
              id={reducedMotionId}
              type="button"
              onClick={handleReducedMotionToggle}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible-enhanced ${
                prefersReducedMotion ? 'bg-blue-600' : 'bg-gray-200'
              }`}
              role="switch"
              aria-checked={prefersReducedMotion}
              aria-describedby={`${reducedMotionId}-description`}
            >
              <span className="sr-only">Toggle reduced motion</span>
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  prefersReducedMotion ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <div id={`${reducedMotionId}-description`} className="sr-only">
            Reduced motion is currently {prefersReducedMotion ? 'enabled' : 'disabled'}
          </div>
        </div>

        {/* Color Scheme */}
        <div className="accessible-form-field">
          <label
            htmlFor={colorSchemeId}
            className="block text-sm font-medium text-gray-900 mb-2"
          >
            Color Scheme
          </label>
          <p className="text-sm text-gray-600 mb-3">
            Choose your preferred color scheme for the application
          </p>
          <fieldset>
            <legend className="sr-only">Color scheme options</legend>
            <div className="space-y-2">
              {[
                { value: 'light', label: 'Light', description: 'Light background with dark text' },
                { value: 'dark', label: 'Dark', description: 'Dark background with light text' },
                { value: 'system', label: 'System', description: 'Follow your device settings' },
              ].map((option) => (
                <div key={option.value} className="flex items-center">
                  <input
                    id={`${colorSchemeId}-${option.value}`}
                    name="colorScheme"
                    type="radio"
                    value={option.value}
                    checked={
                      option.value === 'system'
                        ? !localStorage.getItem('color-scheme')
                        : colorScheme === option.value
                    }
                    onChange={() => handleColorSchemeChange(option.value as 'light' | 'dark' | 'system')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 touch-manipulation"
                  />
                  <label
                    htmlFor={`${colorSchemeId}-${option.value}`}
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    {option.label}
                    <span className="block text-xs text-gray-500 mt-1">
                      {option.description}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </fieldset>
        </div>

        {/* Keyboard Navigation Help */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">
            Keyboard Navigation
          </h3>
          <div className="text-sm text-blue-800 space-y-1">
            <p><kbd className="px-1 py-0.5 bg-blue-100 rounded text-xs">Tab</kbd> - Navigate between elements</p>
            <p><kbd className="px-1 py-0.5 bg-blue-100 rounded text-xs">Enter</kbd> or <kbd className="px-1 py-0.5 bg-blue-100 rounded text-xs">Space</kbd> - Activate buttons and links</p>
            <p><kbd className="px-1 py-0.5 bg-blue-100 rounded text-xs">Escape</kbd> - Close modals and menus</p>
            <p><kbd className="px-1 py-0.5 bg-blue-100 rounded text-xs">Arrow keys</kbd> - Navigate within lists and tables</p>
          </div>
        </div>

        {/* Screen Reader Information */}
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <h3 className="text-sm font-medium text-green-900 mb-2">
            Screen Reader Support
          </h3>
          <p className="text-sm text-green-800">
            This application is optimized for screen readers with proper ARIA labels,
            live regions for dynamic content, and semantic HTML structure.
          </p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onClose}
          className="btn btn-primary focus-visible-enhanced"
        >
          Done
        </button>
      </div>
    </AccessibleModal>
  );
};

/**
 * Floating accessibility button that provides quick access to accessibility settings
 */
export const AccessibilityButton: React.FC = () => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsSettingsOpen(true)}
        className="fixed bottom-4 right-4 z-40 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 focus-visible-enhanced touch-manipulation"
        aria-label="Open accessibility settings"
        title="Accessibility Settings"
      >
        <svg
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      </button>

      <AccessibilitySettings
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
    </>
  );
};